import { useShallow } from 'zustand/react/shallow'

import type { ChannelActions, ChannelState } from './channelSlice'
import type { ComposerProfile } from '~publish/legacy/composer/composer/stores/types'
import { useComposerStore } from '../hooks/useComposerStore'

export const useSelectedChannels = (): ChannelState['selectedChannels'] => {
  return useComposerStore((state) => state.selectedChannels)
}

export const useSelectChannel = (): ChannelActions['selectChannel'] => {
  return useComposerStore((state) => state.selectChannel)
}

export const useDeselectChannel = (): ChannelActions['deselectChannel'] => {
  return useComposerStore((state) => state.deselectChannel)
}

export const useSelectChannels = (): ChannelActions['selectChannels'] => {
  return useComposerStore((state) => state.selectChannels)
}

export const useSelectOnlyChannels = (): ChannelActions['selectOnly'] => {
  return useComposerStore((state) => state.selectOnly)
}

export const useDeselectChannels = (): ChannelActions['deselectChannels'] => {
  return useComposerStore((state) => state.deselectChannels)
}

export const useIsChannelSelectorOpen = (): boolean => {
  return useComposerStore((state) => state.isChannelSelectorOpen)
}

export const useSetChannelSelectorOpen =
  (): ChannelActions['setChannelSelectorOpen'] => {
    return useComposerStore((state) => state.setChannelSelectorOpen)
  }

/**
 * Get all non-disabled profiles (available for selection).
 *
 * Wrapped in `useShallow` because the selector derives a fresh array on every
 * call (`Object.values(...).filter(...)`); without a shallow-equality check the
 * new reference would retrigger `useSyncExternalStore` on each render and loop.
 */
export const useAvailableProfiles = (): ComposerProfile[] => {
  return useComposerStore(
    useShallow((state) =>
      Object.values(state.profiles).filter((profile) => !profile.isDisabled),
    ),
  )
}

/**
 * Get selected channels as ComposerProfile objects (from profiles Record).
 *
 * Wrapped in `useShallow` for the same reason as `useAvailableProfiles`: the
 * `map`/`filter` produces a new array reference each call.
 */
export const useSelectedChannelsList = (): ComposerProfile[] => {
  return useComposerStore(
    useShallow((state) =>
      state.selectedChannels.map((id) => state.profiles[id]).filter(Boolean),
    ),
  )
}
